import 'common/theme/Style.scss';
import React, { lazy, Suspense } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import ProtectedRoute from './common/components/ProtectedRoute';
import BootSkeleton from './common/components/BootSkeleton';
import NotFound from './common/components/NotFound';
import OrdersAuth from './pages/OrdersAuth/OrdersAuth';
import DirectEntry from './dashboard/DirectEntry/DirectEntry';

import { ORDERS_AUTH_ROUTES } from './constants/ordersAuth';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

const Insurance = lazy(() => import(
  /*webpackChunkName: "Insurance"*/ './insurance'
));
const GroupInsurance = lazy(() => import(
  /*webpackChunkName: "GroupInsurance"*/ './group_insurance'
));
const Referral = lazy(() => import(
  /*webpackChunkName: "Referral"*/ './referral'
));
const Gold = lazy(() => import(
  /*webpackChunkName: "Gold"*/ './gold'
));
const Mandate = lazy(() => import(
  /*webpackChunkName: "Mandate"*/ './mandate_address'
));
const Mandate_OTM = lazy(() => import(
  /*webpackChunkName: "Mandate_OTM"*/ './mandate_otm'
));
const EMandate = lazy(() => import(
  /*webpackChunkName: "EMandate"*/ './e_mandate'
));
const RiskProfiler = lazy(() => import(
  /*webpackChunkName: "RiskProfiler"*/ './risk_profiler'
));
const HNI = lazy(() => import(
  /*webpackChunkName: "HNI"*/ './external_portfolio'
));
const IsipBIller = lazy(() => import(
  /*webpackChunkName: "IsipBiller"*/ './isip'
));
const CommonLanding = lazy(() => import(
  /*webpackChunkName: "CommonLanding"*/ './common/components/landing'
));
const Withdraw = lazy(() => import(
  /*webpackChunkName: "Withdraw"*/ './withdraw'
));
const Fhc = lazy(() => import(
  /*webpackChunkName: "Fhc"*/ './fhc'
));
// const Loan = lazy(() => import(
//   /*webpackChunkName: "Loan"*/ './loan_idfc'
// ));
const Payment = lazy(() => import(/*webpackChunkName: "Payment"*/ './payment'));
const KycEsign = lazy(() =>
  import(/*webpackChunkName: "KycEsign"*/ './kyc_esign')
);
const PortfolioRebalancing = lazy(() =>
  import(/*webpackChunkName: "PortfolioRebalancing"*/ './portfolio_rebalancing')
);
const FundDetails = lazy(() =>
  import(/*webpackChunkName: "FundDetails"*/ './fund_details')
);
const Whatsapp = lazy(() =>
  import(/*webpackChunkName: "Whatsapp"*/ './whatsapp')
);
const Landing = lazy(() =>
  import(/*webpackChunkName: "Landing"*/ './dashboard')
);
const FeedBack = lazy(() =>
  import(/*webpackChunkName: "FeedBack"*/ './feedback')
);
const Partner = lazy(() => import(/*webpackChunkName: "Partner"*/ './partner'));
const TaxFiling = lazy(() =>
  import(/*webpackChunkName: "TaxFiling"*/ './tax_filing')
);
const Kyc = lazy(() => import(/*webpackChunkName: "Kyc"*/ './kyc'));
const AccountStatements = lazy(() =>
  import(/*webpackChunkName: "AccountStatements"*/ './account_statements')
);
const EquityPayment = lazy(() =>
  import(/*webpackChunkName: "EquityPayment"*/ './equity_payment')
);
const FreedomPlan = lazy(() =>
  import(/*webpackChunkName: "FreedomPlan"*/ './freedom_plan')
);

const timeout = { enter: 400, exit: 150 };

const Feature = () => {
  // old # route support added
  // start
  const history = useHistory();
  const location = useLocation();

  if (window.location.hash.startsWith('#!/')) {
    history.push(window.location.hash.replace('#!', ''));
  }
  // end

  if (window.location.pathname === '/prepare') {
    return null;
  } else {
    return (
      <ErrorBoundary>
        <Suspense fallback={<BootSkeleton />}>
          <TransitionGroup className="web-css-transition-group">
            <CSSTransition 
              timeout={timeout} 
              classNames="pageSlider"
              mountOnEnter={false}
              unmountOnExit={true} 
              key={location.key}
            >
              {/* change direction here */}
              <div className={`web-transition-direction ${history.action === "POP" ? "right-slide": "left-slide"}`}>
              {/* OPTIMIZATION: Setup base route map constant here which can be used in routeConstants files */}
                <Switch location={location}>
                  <Route path={ORDERS_AUTH_ROUTES.LANDING} component={OrdersAuth}/>
                  <ProtectedRoute path='/insurance' component={Insurance} />
                  <ProtectedRoute path='/group-insurance' component={GroupInsurance} />
                  <ProtectedRoute path='/referral' component={Referral} />
                  <ProtectedRoute path={['/gold','/direct/gold']} component={Gold} />
                  <ProtectedRoute path='/fhc' component={Fhc} />
                  <ProtectedRoute path='/mandate' component={Mandate} />
                  <ProtectedRoute path='/mandate-otm' component={Mandate_OTM} />
                  <ProtectedRoute path='/e-mandate' component={EMandate} />
                  <ProtectedRoute path='/risk' component={RiskProfiler} />
                  <ProtectedRoute path='/hni/' component={HNI} />
                  <ProtectedRoute path='/isip' component={IsipBIller} />
                  <ProtectedRoute exact path={`/direct/:type`} component={DirectEntry} />
                  {/* <ProtectedRoute path='/loan' component={Loan} /> */}
                  <Route path='/pg/eq' component={EquityPayment} />
                  <ProtectedRoute path='/pg' component={Payment} />
                  <ProtectedRoute path='/kyc-esign' component={KycEsign} />
                  <ProtectedRoute path='/whatsapp/' component={Whatsapp} />
                  <ProtectedRoute path='/webview/:main_module/:sub_module' component={CommonLanding} />
                  <ProtectedRoute path='/webview/:main_module' component={CommonLanding} />
                  <ProtectedRoute path='/portfolio-rebalancing' component={PortfolioRebalancing} />
                  <ProtectedRoute path='/fund-details' component={FundDetails} />
                  <ProtectedRoute path='/feedback' component={FeedBack} />
                  <ProtectedRoute path='/tax-filing' component={TaxFiling} />
                  <ProtectedRoute path='/partner' component={Partner} />
                  <ProtectedRoute path='/statements' component={AccountStatements} />
                  <ProtectedRoute path='/withdraw' component={Withdraw} />
                  <ProtectedRoute path='/kyc' component={Kyc} />
                  <ProtectedRoute path='/freedom-plan' component={FreedomPlan} />
                  <ProtectedRoute path='/' component={Landing} />
                  <Route component={NotFound} />
                </Switch>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </Suspense>
      </ErrorBoundary>
    );
  }
};

export default Feature;
